import { useBeaconContext } from './BeaconConnectionWrapper';
import { BeaconPlayerWrapper } from './BeaconPlayerWrapper';
import { QuickstartLoading } from '../quickstart/components/QuickstartLoading';
import { QuickstartCard } from '../quickstart/components/QuickstartCard';
import { lazy, Suspense, useState, useCallback } from 'react';
import { isDebugBuild } from '@playcastdotio/PeerCore';
import { isTuningEligible } from '@playcastdotio/PeerCore/tuning';
import { BEACON_THEME } from './beaconTheme';
import { mediaStreamState } from '../../api/stores/playerStores';
import { SignalQualityIndicator } from '../quickstart/components/SignalQualityIndicator';
import { GuestDebugPanel, type GuestDebugSection } from '../../app/player/debug/GuestDebugPanel';

const BeaconDebugOverlay = lazy(() =>
  import('./components/BeaconDebugOverlay').then((m) => ({ default: m.BeaconDebugOverlay })),
);
const PlayerDebugOverlay = lazy(() =>
  import('../../app/player/quickstart/QuickstartPlayerDebugOverlay').then((m) => ({ default: m.PlayerDebugOverlay })),
);
const StreamDebugSection = lazy(() =>
  import('../../app/player/debug/StreamDebugSection').then((m) => ({ default: m.StreamDebugSection })),
);
const StreamTuningPanel = lazy(() => import('../stream-tuning/components/StreamTuningPanel').then(module => ({ default: module.StreamTuningPanel })));

interface BeaconErrorCardProps {
  title: string;
  message: string;
  buttonLabel: string;
  onAction: () => void;
}

const PAGE_BACKGROUND = '#000000';
const BUTTON_YELLOW = '#FACC15';

const BeaconErrorCard = ({ title, message, buttonLabel, onAction }: BeaconErrorCardProps) => (
  <div
    className='flex min-h-screen items-center justify-center p-4'
    style={{ backgroundColor: PAGE_BACKGROUND }}
  >
    <QuickstartCard theme={BEACON_THEME} hideBadge>
      <div className='flex flex-col items-center gap-4 py-6 text-center'>
        <h1 className='text-xl font-semibold text-white'>{title}</h1>
        <p className='max-w-xs text-zinc-400'>{message}</p>
        <button
          onClick={onAction}
          className='mt-2 rounded-lg px-6 py-2.5 text-sm font-bold transition-opacity hover:opacity-90'
          style={{ backgroundColor: BUTTON_YELLOW, color: '#000' }}
        >
          {buttonLabel}
        </button>
      </div>
    </QuickstartCard>
  </div>
);

export const BeaconJoinPage = () => {
  const { state, coordinator, isDebugMode } = useBeaconContext();
  const [debugMobileGamepad, setDebugMobileGamepad] = useState(false);

  const handleDebugMediaStream = useCallback((videoStream: MediaStream | null, audioStream: MediaStream | null) => {
    mediaStreamState.updateState({
      stream: videoStream || new MediaStream(),
      audioStream: audioStream || new MediaStream(),
    });
  }, []);

  // 'reconnecting_in_place' is a playing session whose transport is being rebuilt, so it
  // renders exactly what 'playing' renders. Keeping the wrapper mounted is the whole point:
  // remounting it would destroy the video element, drop fullscreen and pointer lock, and put
  // the "Click Here To Join" card back in front of a user who never left.
  const isStreamMounted = state.phase === 'playing' || state.phase === 'reconnecting_in_place';

  // One debug surface, on debug builds only, closed by default. It used to be three
  // separately-positioned overlays (stream line, player inspector, beacon phase jump) that
  // stacked over the top of the page and whose close buttons a touch could not reach.
  // Requires both a debug build and `?debug`. The stream and player sections only
  // exist while the stream is mounted.
  const debugSections: GuestDebugSection[] = [
    ...(isStreamMounted
      ? [
          {
            id: 'stream',
            label: 'Stream',
            content: (
              <Suspense fallback={null}>
                <StreamDebugSection />
              </Suspense>
            ),
          },
          {
            id: 'player',
            label: 'Player',
            content: (
              <Suspense fallback={null}>
                <PlayerDebugOverlay
                  embedded
                  sessionId={state.sessionId}
                  accentColor={BEACON_THEME.primaryColor}
                  extraSessionRows={[{ label: 'Share Code', value: state.shareCode }]}
                  mobileGamepadEnabled={debugMobileGamepad}
                  onToggleMobileGamepad={() => setDebugMobileGamepad((prev) => !prev)}
                  onDebugMediaStream={handleDebugMediaStream}
                />
              </Suspense>
            ),
          },
        ]
      : []),
    {
      id: 'beacon',
      label: 'Beacon',
      content: (
        <Suspense fallback={null}>
          <BeaconDebugOverlay
            embedded
            currentPhase={state.phase}
            sessionId={state.sessionId}
            onPhaseChange={(phase) => coordinator?.transitionTo(phase)}
          />
        </Suspense>
      ),
    },
  ];
  const developerControls = isDebugBuild() && isDebugMode;
  const tuningEligible = isTuningEligible(isDebugBuild(), window.location.search);
  const visibleDebugSections = [...(developerControls ? debugSections : []), ...(tuningEligible ? [{
    id: 'tuning', label: 'Tuning', content: <Suspense fallback={null}><StreamTuningPanel /></Suspense>,
  }] : [])];
  const debugOverlay = visibleDebugSections.length ? (
    <GuestDebugPanel sections={visibleDebugSections} accentColor={BEACON_THEME.primaryColor} />
  ) : null;

  const handleReload = () => window.location.reload();

  // Pre-stream and terminal phases have no peer transport, so the HUD reports
  // "no signal" here. BeaconPlayerWrapper mounts its own instance backed by live
  // store data once the phase reaches 'playing'. Every non-'playing' branch below
  // must render this: the HUD is contractually always-on, and a phase that omits
  // it silently deletes the one piece of UI that explains why the stream stopped.
  // Rendered as a sibling of QuickstartLoading / BeaconErrorCard (never a child)
  // so its `fixed` positioning is not captured by their transforms.
  const noSignalIndicator = <SignalQualityIndicator level={null} connectionDown />;

  switch (state.phase) {
    case 'page_viewed':
    case 'fast_starting':
      return (
        <>
          <QuickstartLoading theme={BEACON_THEME} message='Connecting to host...' badgeText={state.streamTitle} />
          {noSignalIndicator}
          {debugOverlay}
        </>
      );

    case 'connecting_peer':
      return (
        <>
          <QuickstartLoading theme={BEACON_THEME} message='Establishing stream...' badgeText={state.streamTitle} />
          {noSignalIndicator}
          {debugOverlay}
        </>
      );

    case 'playing':
    case 'reconnecting_in_place':
      return (
        <>
          <BeaconPlayerWrapper debugMobileGamepad={debugMobileGamepad} />
          {debugOverlay}
        </>
      );

    case 'not_found':
      return (
        <>
          <BeaconErrorCard
            title='Host Not Found'
            message='This link is no longer active. The host may have gone offline or the share code has expired.'
            buttonLabel='Go to Playcast'
            onAction={() => { window.location.href = 'https://playcast.io'; }}
          />
          {noSignalIndicator}
          {debugOverlay}
        </>
      );

    case 'kicked':
      return (
        <>
          <BeaconErrorCard
            title='Removed by Host'
            message='The host has ended your session.'
            buttonLabel='Back to Playcast'
            onAction={() => { window.location.href = 'https://playcast.io'; }}
          />
          {noSignalIndicator}
          {debugOverlay}
        </>
      );

    case 'disconnected':
      return (
        <>
          <BeaconErrorCard
            title='Disconnected'
            message='Your connection to the host was lost.'
            buttonLabel='Reconnect'
            onAction={handleReload}
          />
          {noSignalIndicator}
          {debugOverlay}
        </>
      );

    case 'error':
      return (
        <>
          <BeaconErrorCard
            title='Connection Error'
            message='Something went wrong while connecting to the host.'
            buttonLabel='Try Again'
            onAction={handleReload}
          />
          {noSignalIndicator}
          {debugOverlay}
        </>
      );

    default:
      return null;
  }
};
